import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, onSnapshot, collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import Layout from "../components/Layout";
import { FaPlay, FaClock, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

export default function CloudFunctionManager() {
  const [functions, setFunctions] = useState([
    { 
      id: "autoCarryForwardStock", 
      name: "Daily Stock Carry-Forward", 
      description: "Moves yesterday's closing stock to today's opening stock.",
      schedule: "00:05 AM IST",
      lastStatus: "Unknown",
      lastRun: null
    },
    { 
      id: "autoCarryForwardLedger", 
      name: "Salesman Ledger Auto-Sync", 
      description: "Finalizes yesterday's ledger, pulls Online Collections from Sheets, and marks orders as Synced.",
      schedule: "01:05 AM IST",
      lastStatus: "Unknown",
      lastRun: null
    },
    { 
      id: "syncOutletOrdersToBQ", 
      name: "BigQuery Data Archival", 
      description: "Pushes daily outlet orders to BigQuery for long-term reporting.",
      schedule: "01:05 AM IST",
      lastStatus: "Unknown",
      lastRun: null
    }
  ]);

  const [triggering, setTriggering] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    // 1. Fetch config/last-runs
    const unsub = onSnapshot(doc(db, "config", "cloud_functions"), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setFunctions(prev => prev.map(f => ({
          ...f,
          lastStatus: data[f.id]?.status || "Idle",
          lastRun: data[f.id]?.lastRunAt?.toDate?.() || null
        })));
      }
    });

    return () => unsub();
  }, []);

  const triggerFunction = async (funcId) => {
    if (!window.confirm(`Are you sure you want to trigger ${funcId} manually? This will process data for the current/previous day immediately.`)) return;
    
    setTriggering(funcId);
    try {
      const functionsInstance = getFunctions();
      const callable = httpsCallable(functionsInstance, `manualTrigger_${funcId}`);
      const result = await callable();
      alert(`Success: ${result.data.message || "Function executed successfully."}`);
    } catch (err) {
      console.error(err);
      alert(`Error triggering function: ${err.message}`);
    }
    setTriggering(null);
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    border: "1px solid #edf2f7",
    position: "relative",
    overflow: "hidden"
  };

  return (
    <Layout>
      <div style={{ padding: "40px", background: "#f8fafc", minHeight: "100vh" }}>
        <div style={{ marginBottom: "40px" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 800, color: "#1e293b", margin: 0 }}>Cloud Control Center</h2>
          <p style={{ color: "#64748b", fontSize: "16px", marginTop: "4px" }}>Monitor and manually trigger scheduled background tasks.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: "24px" }}>
          {functions.map(f => (
            <div key={f.id} style={cardStyle}>
              {triggering === f.id && (
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
                   <div style={{ textAlign: "center" }}>
                      <div className="spinner" style={{ border: "4px solid #f3f3f3", borderTop: "4px solid #3182ce", borderRadius: "50%", width: "40px", height: "40px", animation: "spin 1s linear infinite", margin: "0 auto 10px" }}></div>
                      <p style={{ fontWeight: 600, color: "#3182ce" }}>Triggering...</p>
                   </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "#ebf8ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#3182ce" }}>
                  <FaClock size={20} />
                </div>
                <div style={{ textAlign: "right" }}>
                   <span style={{ fontSize: "11px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>Fixed Schedule</span>
                   <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>{f.schedule}</div>
                </div>
              </div>

              <div>
                <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: "8px 0 4px" }}>{f.name}</h3>
                <p style={{ fontSize: "13px", color: "#64748b", lineHeight: "1.5", minHeight: "40px" }}>{f.description}</p>
              </div>

              <div style={{ marginTop: "10px", padding: "12px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", color: "#94a3b8" }}>Last Run Status</span>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: f.lastStatus === "Success" ? "#10b981" : "#f59e0b" }}>{f.lastStatus}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12px", color: "#94a3b8" }}>Executed At</span>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>{f.lastRun ? f.lastRun.toLocaleString() : "Never"}</span>
                </div>
              </div>

              <button 
                onClick={() => triggerFunction(f.id)}
                disabled={triggering !== null}
                style={{ 
                  marginTop: "12px",
                  background: "#1e293b",
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.target.style.background = "#0f172a"}
                onMouseLeave={(e) => e.target.style.background = "#1e293b"}
              >
                <FaPlay size={12} /> Run Now
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "40px", padding: "24px", background: "#fffef0", border: "1px solid #fef3c7", borderRadius: "16px" }}>
          <div style={{ display: "flex", gap: "15px" }}>
            <FaExclamationTriangle style={{ color: "#d97706", fontSize: "24px", marginTop: "4px" }} />
            <div>
              <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#92400e" }}>Important Notice for Developers</h4>
              <p style={{ margin: "5px 0 0 0", fontSize: "13px", color: "#b45309", lineHeight: "1.6" }}>
                Manual triggers bypass the normal 01:05 AM schedule but use the same logic. 
                Running a function multiple times is safe (idempotent), but frequent manual runs may impact your Online Collection consumption limits from Google Sheets.
                <b> Note:</b> To change the <i>timing</i> of the automated runs, please update the cron schedule in <code>functions/index.js</code> and redeploy.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
}
